import React from 'react';
import { Loading } from '@brighterion/br-ui';
import axios from 'axios';
import { useAuthenticationStore } from 'Hooks/zustand/useAuthenticationStore';
import { useAccessTokenStore } from 'Hooks/zustand/useAccessTokenStore';
import { useSelector } from 'react-redux';
import {
    PCI_VALUE_INPUT,
    LOADED,
    INIT,
    GET_TOKEN,
    GET_TOKEN_SUCCESS,
    INPUT_CHANGE,
    GET_TOKEN_ERROR,
    INPUT_CLEAR
} from 'Utils';
import { appInfoSelector, getUser } from 'Selectors';
import { useTranslation } from 'react-i18next';
import Iframe from 'Components/Iframe';
import useReduxModal from 'Hooks/reduxHelperHooks/useReduxModal';
import SearchButton from './SearchButton';
import styles from './ListEntryTable.css';

type Props = {
    onSubmit(thisValue: string): void;
    placeholderText: string;
    isLoading: boolean;
};

const IFRAME_NAME = `${PCI_VALUE_INPUT}-search`;

const PCISearch = ({ onSubmit, placeholderText, isLoading }: Props) => {
    const { t } = useTranslation();
    const appInfo = useSelector(appInfoSelector);
    const { customerId } = useSelector(getUser);
    const iframeRef = React.useRef<HTMLIFrameElement>(null);
    const [loaded, setLoaded] = React.useState(false);
    const [isValid, setIsValid] = React.useState(false);
    const [fetchingToken, setFetchingToken] = React.useState(false);
    const isFetching = isLoading || fetchingToken;
    const { addTechnicalErrorModal } = useReduxModal();

    React.useEffect(() => {
        const listener = (e: MessageEvent) => {
            // do this to get origin
            const { origin } = new URL(appInfo!.pciComponentsURL);

            if (e.origin !== origin) return;
            if (e?.data) {
                const pData = e?.data;
                if (pData?.name === IFRAME_NAME) {
                    switch (pData?.type) {
                        case LOADED:
                            setLoaded(true);
                            break;
                        case GET_TOKEN_SUCCESS:
                            onSubmit(pData.payload);
                            setFetchingToken(false);
                            break;
                        case INPUT_CHANGE:
                            setIsValid(Boolean(pData.payload.isValid));
                            break;
                        case GET_TOKEN_ERROR:
                            addTechnicalErrorModal(
                                t('pciInputError'),
                                t('Error Saving'),
                                async () => {
                                    /**
                                     * @note Refreshing the access token here is temp a solution.
                                     *
                                     * Since PAN search, field and uploader renders an iframe that
                                     * is uses the access token in the payload, refreshing
                                     * the access token inside the iframe is not working for now.
                                     * To make sure the user will have at least 10 minutes
                                     * before the access token expire, refreshing the tokens
                                     * happen when the user reloading the page.
                                     *
                                     * Impacted components:
                                     * - BcatsMaskedField
                                     * - PCISearch
                                     * - PCIFileUploader
                                     * - PCIInput
                                     */
                                    const { authService } = useAuthenticationStore.getState();
                                    await authService?.refreshAccessToken();
                                    useAccessTokenStore.setState({ accessToken: await authService?.getAccessToken() });

                                    window.location.reload();
                                },
                                {
                                    cancelText: t('Reload page')
                                }
                            );
                            setFetchingToken(false);
                            break;
                        case INPUT_CLEAR: {
                            setIsValid(false);
                            break;
                        }
                        default:
                            break;
                    }
                }
            }
        };
        window.addEventListener('message', listener);
        return () => {
            window.removeEventListener('message', listener);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [onSubmit]);

    React.useEffect(() => {
        if (loaded) {
            iframeRef.current?.contentWindow?.postMessage(
                {
                    type: INIT,
                    payload: {
                        jwt: axios.defaults.headers.common.Authorization,
                        placeholderText,
                        tenantId: customerId,
                        type: 'SEARCHBAR'
                    }
                },
                iframeRef.current.src
            );
        }
    }, [loaded, placeholderText, customerId]);

    const getToken = () => {
        setFetchingToken(true);
        const thisIframe = iframeRef.current;
        thisIframe?.contentWindow?.postMessage({ type: GET_TOKEN }, thisIframe?.src);
    };

    return (
        <div className={styles.searchContainer} data-testid='pci-search-input'>
            <div className={styles.searchInput}>
                {!loaded && (
                    <div className={styles.loadingContainer}>
                        <Loading size='sm' />
                    </div>
                )}
                <Iframe
                    ref={iframeRef}
                    name={IFRAME_NAME}
                    id={IFRAME_NAME}
                    src={`${appInfo!.pciComponentsURL}/tokenize_single`}
                />
            </div>
            <SearchButton onSubmit={() => getToken()} isLoading={isFetching} isDisabled={!isValid || isLoading} />
        </div>
    );
};

export default React.memo(PCISearch);
